import axios, { type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import { getToken } from './keycloak';
import { env } from './env';

// API base URL
const API_BASE_URL = env.VITE_API_BASE_URL;

// Seahorse Console API URL
const SEAHORSE_CONSOLE_URL = env.VITE_SEAHORSE_CONSOLE_URL;

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const token = await getToken();
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid, redirect to login
      window.location.href = '/';
    }
    return Promise.reject(error);
  }
);

export default apiClient;

// API client wrapper functions
export const api = {
  // Personal Info
  getPersonalInfo: async () => {
    const response = await apiClient.get('/?userProfileMetadata=true');
    return response.data;
  },

  savePersonalInfo: async (data: unknown) => {
    const response = await apiClient.post('/', data);
    return response.data;
  },

  getSupportedLocales: async () => {
    const response = await apiClient.get('/supportedLocales');
    return response.data;
  },

  // Sessions
  getDevices: async () => {
    const response = await apiClient.get('/sessions/devices');
    return response.data;
  },

  deleteAllSessions: async () => {
    const response = await apiClient.delete('/sessions');
    return response.data;
  },

  deleteSession: async (sessionId: string) => {
    const response = await apiClient.delete(`/sessions/${sessionId}`);
    return response.data;
  },

  // Applications
  getApplications: async () => {
    const response = await apiClient.get('/applications');
    return response.data;
  },

  deleteConsent: async (clientId: string) => {
    const response = await apiClient.delete(`/applications/${clientId}/consent`);
    return response.data;
  },

  // Linked Accounts
  getLinkedAccounts: async (params?: {
    first?: number;
    max?: number;
    search?: string;
    linked?: boolean;
  }) => {
    const response = await apiClient.get('/linked-accounts', { params });
    return response.data;
  },

  unlinkAccount: async (providerName: string) => {
    const response = await apiClient.delete(`/linked-accounts/${providerName}`);
    return response.data;
  },

  // Groups
  getGroups: async () => {
    const response = await apiClient.get('/groups');
    return response.data;
  },

  // Organizations
  getOrganizations: async () => {
    const response = await apiClient.get('/organizations');
    return response.data;
  },

  // Permissions
  getPermissionRequests: async (resourceId: string) => {
    const response = await apiClient.get(`/resources/${resourceId}/permissions/requests`);
    return response.data;
  },

  // Change Password
  changePassword: async (currentPassword: string, newPassword: string) => {
    const token = await getToken();
    if (!token) {
      throw new Error('Authentication token not available');
    }
    const response = await axios.post(
      `${SEAHORSE_CONSOLE_URL}/api/users/change-password`,
      {
        current_password: currentPassword,
        new_password: newPassword,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );
    return response.data;
  },

  // Account Withdrawal
  withdraw: async (email: string) => {
    const token = await getToken();
    console.log('Withdraw - token obtained:', token ? `${token.substring(0, 20)}...` : 'undefined');
    if (!token) {
      throw new Error('Authentication token not available');
    }
    const response = await axios.post(
      `${SEAHORSE_CONSOLE_URL}/api/users/withdraw`,
      { email },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );
    return response.data;
  },
};
